import { toAsyncIterator } from "./asyncIterable";
import { pickWritableFile, supportsFileSystemWriter } from "./fileSystemAccess";
import { sanitizeFileName } from "./sanitizeFileName";
import { writeZipToFileInWorker, zipToBlobInWorker } from "@/workers/downloads/client";

export { supportsFileSystemWriter };

export type DownloadProgress = {
  /** Cumulative count of content chunks consumed so far. */
  chunksProcessed: number;
  /** Cumulative character count consumed so far. */
  charsProcessed: number;
};

export type DownloadFileOptions = {
  /**
   * Filename for the downloaded file, including extension (e.g. "report.json", "data.csv").
   * When `zip` is true, this is the entry name *inside* the archive — the file actually saved to
   * disk gets this same name with its extension swapped for ".zip". Always run through
   * `sanitizeFileName` internally, so callers don't need to sanitize it themselves first.
   */
  filename: string;
  /**
   * File content — a single string for content already fully in memory, or a chunk iterable
   * (e.g. `generateJsonChunks`'s output from jsonBlob.ts) to stream very large content without
   * ever building one giant string upfront.
   */
  content: string | Iterable<string> | AsyncIterable<string>;
  /**
   * MIME type for the Blob-fallback download path. Ignored when `zip` is true (the download is
   * always "application/zip" there). Required otherwise — every existing call site already knows
   * its own precise MIME type, so this isn't guessed from the filename.
   */
  mimeType?: string;
  /**
   * Compress into a zip archive before downloading, with compression offloaded to a Web Worker —
   * worth it for large or highly repetitive content (see zip.ts's benchmark comment for why).
   * Defaults to false so small/simple downloads aren't forced through the extra work.
   */
  zip?: boolean;
  /** Fires as content is consumed. Mainly meaningful for large/streamed `content`. */
  onProgress?: (progress: DownloadProgress) => void;
};

function toChunks(content: DownloadFileOptions["content"]): Iterable<string> | AsyncIterable<string> {
  return typeof content === "string" ? [content] : content;
}

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(dot) : "";
}

function toZipFilename(filename: string): string {
  const dot = filename.lastIndexOf(".");
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  return `${base}.zip`;
}

async function* withProgress(
  chunks: Iterable<string> | AsyncIterable<string>,
  onProgress: ((progress: DownloadProgress) => void) | undefined,
): AsyncGenerator<string> {
  const iterator = toAsyncIterator(chunks);
  let chunksProcessed = 0;
  let charsProcessed = 0;

  let current = await iterator.next();
  while (!current.done) {
    if (onProgress) {
      chunksProcessed++;
      charsProcessed += current.value.length;
      onProgress({ chunksProcessed, charsProcessed });
    }
    yield current.value;
    current = await iterator.next();
  }
}

async function writeTextChunksToFile(
  chunks: Iterable<string> | AsyncIterable<string>,
  suggestedName: string,
  mimeType: string,
): Promise<void> {
  const ext = extensionOf(suggestedName);
  const writable = await pickWritableFile(suggestedName, [{ description: mimeType, accept: { [mimeType]: ext ? [ext] : [] } }]);

  try {
    const iterator = toAsyncIterator(chunks);
    let current = await iterator.next();
    while (!current.done) {
      await writable.write(current.value);
      current = await iterator.next();
    }
  } catch (error) {
    await writable.abort(error);
    throw error;
  }

  await writable.close();
}

async function textChunksToBlob(chunks: Iterable<string> | AsyncIterable<string>, mimeType: string): Promise<Blob> {
  const parts: string[] = [];
  const iterator = toAsyncIterator(chunks);
  let current = await iterator.next();
  while (!current.done) {
    parts.push(current.value);
    current = await iterator.next();
  }
  return new Blob(parts, { type: mimeType });
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * `"completed"` — the file was written/downloaded. `"cancelled"` — the user closed the native
 * save-file dialog without picking a location; not an error, but distinct from completing, since
 * a caller driving its own progress UI (a toast, say) needs to know not to show a success message.
 */
export type DownloadResult = "completed" | "cancelled";

/**
 * Downloads `content` to the user's machine as `filename` — the shared entry point for
 * client-side downloads in this app, wrapping the File System Access / zip / Web Worker
 * infrastructure in `fileSystemAccess.ts`, `zip.ts`, and `workers/downloads/`.
 *
 * Uses the File System Access API (native "Save As" dialog, streamed straight to disk) where the
 * browser supports it, falling back to the classic Blob + anchor-click trick otherwise (e.g.
 * Firefox/Safari).
 */
export async function downloadFile({
  filename: rawFilename,
  content,
  mimeType,
  zip = false,
  onProgress,
}: DownloadFileOptions): Promise<DownloadResult> {
  const filename = sanitizeFileName(rawFilename);
  const chunks = toChunks(content);

  try {
    if (zip) {
      const zipFilename = toZipFilename(filename);
      if (supportsFileSystemWriter()) {
        await writeZipToFileInWorker(filename, withProgress(chunks, onProgress), zipFilename);
      } else {
        const blob = await zipToBlobInWorker(filename, withProgress(chunks, onProgress));
        triggerBlobDownload(blob, zipFilename);
      }
      return "completed";
    }

    if (!mimeType) {
      throw new Error(`downloadFile: "mimeType" is required when zip is false (filename: "${filename}")`);
    }

    const trackedChunks = withProgress(chunks, onProgress);
    if (supportsFileSystemWriter()) {
      await writeTextChunksToFile(trackedChunks, filename, mimeType);
    } else {
      const blob = await textChunksToBlob(trackedChunks, mimeType);
      triggerBlobDownload(blob, filename);
    }
    return "completed";
  } catch (error) {
    if ((error as DOMException)?.name === "AbortError") return "cancelled"; // user cancelled the save dialog
    throw error;
  }
}
